class TabCarousel extends HTMLElement {
  activeTabIndex = 0;
  maxTabIndex = 0;
  namespace = null;
  tabsHolder = null;
  tabList = null;
  tabs = [];
  tabPanelList = null;
  tabPanels = [];
  tabControls = [];

  constructor() {
    super();

    this.namespace = this.id || 'tab-carousel';
    this.setAttribute('data-ready', true);

    this.tabsHolder = this.querySelector('.tab-carousel__tabs');
    this.tabList = this.querySelector('.tab-carousel__tablist');
    this.tabs = [...this.querySelectorAll('.tab-carousel__tab')];
    this.tabPanelList = this.querySelector('.tab-carousel__panels');
    this.tabPanels = [...this.querySelectorAll('.tab-carousel__panel')];
    this.tabControls = [...this.querySelectorAll('.tab-carousel__control')];

    // If we don't have any tabs, bail
    if (!this.tabs.length) {
      return;
    }

    // If more tabs than panels, remove excess tabs from the DOM
    if (this.tabs.length > this.tabPanels.length) {
      for (let i = this.tabPanels.length; i < this.tabs.length; i++) {
        this.tabs[i].parentNode.removeChild(this.tabs[i]);
      }
      this.tabs = this.tabs.slice(0, this.tabPanels.length);
    }

    // If more panels than tabs, remove excess panels from the DOM
    if (this.tabPanels.length > this.tabs.length) {
      for (let i = this.tabs.length; i < this.tabPanels.length; i++) {
        this.tabPanels[i].parentNode.removeChild(this.tabPanels[i]);
      }
      this.tabPanels = this.tabPanels.slice(0, this.tabs.length);
    }

    this.maxTabIndex = this.tabs.length - 1;

    this._onTabClick = this.onTabClick.bind(this);
    this._onControlClick = this.onControlClick.bind(this);
    this._onTabKeyDown = this.onTabKeyDown.bind(this);
    this._onPanelKeyDown = this.onPanelKeyDown.bind(this);

    this.setupTabs();
    this.onTabClick(0, true);
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  addEventListeners() {
    this.tabs.forEach((tab, i) => {
      tab._onTabClick = this._onTabClick.bind(this, i);
      tab._onTabKeyDown = this._onTabKeyDown.bind(this, i);
      tab.addEventListener('click', tab._onTabClick);
      tab.addEventListener('keydown', tab._onTabKeyDown);
    });

    this.tabPanels.forEach((panel, i) => {
      panel._onPanelKeyDown = this._onPanelKeyDown.bind(this, i);
      panel.addEventListener('keydown', panel._onPanelKeyDown);
    });

    this.tabControls.forEach((control, i) => {
      control._onControlClick = this._onControlClick.bind(
        this,
        control.getAttribute('data-carousel-dir'),
      );
      control.addEventListener('click', control._onControlClick);
    });
  }
  removeEventListeners() {
    this.tabs.forEach((tab, i) => {
      tab.removeEventListener('click', tab._onTabClick);
      tab.removeEventListener('keydown', tab._onTabKeyDown);
    });

    this.tabPanels.forEach((panel, i) => {
      panel.removeEventListener('keydown', panel._onPanelKeyDown);
    });

    this.tabControls.forEach(control => {
      control.removeEventListener('click', this._onControlClick);
    });
  }

  setupTabs() {
    if (this.tabList) {
      this.tabList.setAttribute('role', 'tablist');

      if (this.tabs.length > 1) {
        this.tabsHolder.removeAttribute('hidden');
      }
    }

    this.tabs.forEach((tab, i) => {
      tab.id = `${this.namespace}-tab-${i}`;
      tab.setAttribute('role', 'tab');
    });

    if (this.tabPanelList) {
      this.tabPanelList.classList.remove('flow');
    }

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.id = `${this.namespace}-tabpanel-${i}`;
      panel.setAttribute('aria-labelledby', this.tabs[i].id);
    });

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-controls', this.tabPanels[i].id);
    });

    this.updateTabs();
  }

  updateTabs() {
    if (this.activeTabIndex < 0) {
      this.activeTabIndex = this.maxTabIndex;
    }

    if (this.activeTabIndex > this.maxTabIndex) {
      this.activeTabIndex = 0;
    }

    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', i === this.activeTabIndex);
      tab.setAttribute('tabindex', i === this.activeTabIndex ? 0 : -1);
    });

    this.tabPanels.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i !== this.activeTabIndex);
      if (i !== this.activeTabIndex) {
        panel.removeAttribute('tabindex');
      } else {
        panel.setAttribute('tabindex', -1);
      }
    });
  }

  onTabClick(index, isInitial = false) {
    this.activeTabIndex = index;
    this.updateTabs();

    if (!isInitial) {
      this.tabs[this.activeTabIndex].focus();
    }
  }

  onControlClick(direction = 1) {
    this.activeTabIndex += direction * 1;
    this.updateTabs();
  }

  checkNextKey(e) {
    return 'ArrowRight' === e.key;
  }

  checkPrevKey(e) {
    return 'ArrowLeft' === e.key;
  }

  checkFocusInKey(e) {
    return 'ArrowUp' === e.key;
  }

  checkFocusOutKey(e) {
    return 'ArrowDown' === e.key;
  }

  onTabKeyDown(index, e) {
    if (e.key === 'Enter' || e.key === ' ') {
      this.activeTabIndex = index;
      this.updateTabs();

      return;
    }

    if (this.checkNextKey(e)) {
      this.onTabClick(index + 1);
      return;
    }

    if (this.checkPrevKey(e)) {
      this.onTabClick(index - 1);
      return;
    }

    if (this.checkFocusInKey(e)) {
      this.tabPanels[this.activeTabIndex].focus();
    }
  }

  onPanelKeyDown(index, e) {
    if (this.checkFocusOutKey(e)) {
      this.tabs[this.activeTabIndex].focus();
    }
  }
}

customElements.define('tab-carousel', TabCarousel);
